'use client';

import { useTranslation } from 'react-i18next';

import { cn } from '@kit/ui/cn';
import { InfiniteScrollList } from '@kit/ui/infinite-scroll-list';
import { Trans } from '@kit/ui/trans';

import type { DancerListItem } from '../_lib/server/get-dancers';
import { loadMoreDancersAction } from '../_lib/server/load-more-action';
import { DancerCard } from './dancer-card';

type LoadMoreFn = (params: {
  page: number;
  gender?: string;
  danceLevel?: string;
  search?: string;
  danceStyle?: string;
}) => Promise<{ dancers: DancerListItem[]; hasMore: boolean }>; // i18n-ignore TypeScript generic, not user-facing

// First N cards above the fold get priority loading — the rest are lazy.
const PRIORITY_COUNT = 8;

export interface DancersLoadMoreProps {
  /**
   * SSR page 0. Rendered in the SAME grid as the later pages so the responsive
   * grid back-fills across the page boundary (BAL-524).
   */
  initialDancers: DancerListItem[];
  initialHasMore: boolean;
  filters: {
    gender?: string;
    danceLevel?: string;
    search?: string;
    danceStyle?: string;
  };
  loadMoreAction?: LoadMoreFn;
  /** Dims the grid while a filter navigation is in flight. */
  isLoading?: boolean;
  /** Super-admin only (BAL-392) — forwarded to every DancerCard, so later
   * pages carry the same Edit affordance as page 0. */
  showAdminEdit?: boolean;
}

export function DancersLoadMore({
  initialDancers,
  initialHasMore,
  filters,
  loadMoreAction = loadMoreDancersAction,
  isLoading = false,
  showAdminEdit = false,
}: DancersLoadMoreProps) {
  const { t } = useTranslation();

  // The list renders nothing for zero items, so an empty first page with
  // nothing more to load keeps the explicit empty state.
  if (initialDancers.length === 0 && !initialHasMore && !isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center px-4">
        <div className="max-w-sm space-y-2 text-center">
          <p className="text-foreground text-base font-medium">
            <Trans i18nKey="dancers:empty.title" defaults="No dancers found" />
          </p>
          <p className="text-muted-foreground text-sm">
            <Trans
              i18nKey="dancers:empty.subtitle"
              defaults="Try adjusting your filters"
            />
          </p>
        </div>
      </div>
    );
  }

  return (
    <InfiniteScrollList<DancerListItem>
      initialItems={initialDancers}
      initialItemKeys={initialDancers.map((dancer) => dancer.id)}
      initialHasMore={initialHasMore}
      loadMore={async (page) => {
        const result = await loadMoreAction({
          page,
          gender: filters.gender,
          danceLevel: filters.danceLevel,
          search: filters.search,
          danceStyle: filters.danceStyle,
        });
        return { items: result.dancers, hasMore: result.hasMore };
      }}
      renderItem={(dancer, index) => (
        <DancerCard
          key={dancer.id}
          dancer={dancer}
          priority={index < PRIORITY_COUNT}
          showAdminEdit={showAdminEdit}
        />
      )}
      getItemKey={(dancer) => dancer.id}
      gridClassName={cn(
        'grid grid-cols-2 gap-x-4 gap-y-6 px-4 transition-opacity duration-200 sm:grid-cols-3 sm:gap-x-5 sm:gap-y-8 md:grid-cols-4 lg:grid-cols-5',
        isLoading && 'opacity-60',
      )}
      trigger="auto"
      ariaLabel={t('community:dancers.listAriaLabel')}
      loadMoreLabel={
        <Trans i18nKey="dancers:loadMore" defaults="Load more dancers" />
      }
    />
  );
}
